import React from 'react'
import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'
import { format } from 'date-fns'
import styled from '@emotion/styled'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import { config } from '../../config'
import { requestDetailsSchema } from '../../schemas/foiSchema'
import { emptyRequestDetails, getFoiStepperArray } from '../../util/foiConsts'
import { getLegislation } from '../../util/legislation'
import {
  Form,
  DarkBGPaddedFormDiv,
  FormSectionDiv,
  FlexEndButtonErrorContainer,
  SpaceBetweenRowContainer,
  FlexEndContainer,
  FlexColumn
} from '../styles/Containers'
import { WarningMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import BodyText from '../styles/Text'
import InformationTypeForm from '../foiForms/InformationTypeForm'
import RequestDetailsForm from '../foiForms/RequestDetailsForm'
import FOIDocumentAccessForm from '../foiForms/FOIDocumentAccessForm'
import Carousel from '../common/Carousel'

const NavButtonContainer = styled(FlexEndContainer)`
  text-wrap: nowrap;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`

function RequestDetailsStep({ requestDetails = emptyRequestDetails, onNavigate = () => {}, disableDigital = false }) {
  const legislation = getLegislation()
  const defaultValues = { ...requestDetails }
  if (requestDetails.thirdPartyFiles) defaultValues.thirdPartyFiles = requestDetails.thirdPartyFiles
  if (requestDetails.addresses) {
    defaultValues.addresses = requestDetails.addresses.map(address => {
      const addressClone = { ...address }
      if (address.files) addressClone.files = address.files
      return addressClone
    })
  }

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(requestDetailsSchema),
    defaultValues
  })
  const { handleSubmit, getValues } = methods

  const onError = (errors, e) => console.error('Error', errors, e)

  function onSubmit(data) {
    const requestData = structuredClone(data)
    if (data.thirdPartyFiles) requestData.thirdPartyFiles = data.thirdPartyFiles
    if (data.addresses) {
      requestData.addresses = data.addresses.map(address => {
        const clonedAddress = { ...address }
        if (address.files) clonedAddress.files = address.files
        return clonedAddress
      })
    }
    if (!requestData.hasAddresses) requestData.addresses = []
    if (!requestData.infoType || !requestData.infoType.includes('THIRD_PARTY')) requestData.thirdPartyFiles = []
    if (requestData.periodStart instanceof Date) requestData.periodStart = format(requestData.periodStart, 'MMM-dd-yyyy')
    if (requestData.periodEnd instanceof Date) requestData.periodEnd = format(requestData.periodEnd, 'MMM-dd-yyyy')

    onNavigate(requestData, 'next')
  }

  return (
    <FlexColumn>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit, onError)} gap="30px">
          <SpaceBetweenRowContainer gap="50px">
            <NavButtonContainer>
              <SecondaryButton padding="large" onClick={() => onNavigate(getValues(), 'back')}>
                <ArrowBackIosIcon />
                Edit Contact
              </SecondaryButton>
            </NavButtonContainer>
            <Carousel steps={getFoiStepperArray(config.foi.APPLICATION_FEE)} activeStep={1} />
            <NavButtonContainer>
              <Button padding="large" type="submit">
                Review
                <ArrowForwardIosIcon />
              </Button>
            </NavButtonContainer>
          </SpaceBetweenRowContainer>
          <DarkBGPaddedFormDiv>
            <WarningMessage>
              The {legislation.fullName} can only be used to request copies of recorded information, not to pose questions to be responded to. Please
              phrase your request accordingly. Include the date or time frame for the records if applicable and be as specific as possible. This will
              assist us in responding to your request. Please also specify any reference or file number(s), if known.
            </WarningMessage>
            <FormSectionDiv>
              <InformationTypeForm />
            </FormSectionDiv>
            <FormSectionDiv>
              <RequestDetailsForm />
            </FormSectionDiv>
            <FormSectionDiv>
              <FOIDocumentAccessForm disableDigital={disableDigital} />
            </FormSectionDiv>
            <FlexEndButtonErrorContainer>
              <Button padding="large" type="submit">
                Review
                <ArrowForwardIosIcon />
              </Button>
            </FlexEndButtonErrorContainer>
            <BodyText>{config.foi.request.disclaimer}</BodyText>
          </DarkBGPaddedFormDiv>
        </Form>
      </FormProvider>
    </FlexColumn>
  )
}

RequestDetailsStep.propTypes = {
  requestDetails: PropTypes.object,
  onNavigate: PropTypes.func,
  disableDigital: PropTypes.bool
}

export default RequestDetailsStep
